import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Botao, Tooltip } from 'src/componentes'
import { Breadcrumb } from 'src/componentes/breadcrumb'
import { Cabecalho } from 'src/componentes/cabecalho'
import { Cores } from 'src/componentes/styles/cores'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import {
  ServiceHealthState,
  ServiceSummaryResponse,
  SERVICE_HEALTH_STATE_TEMA
} from 'src/dto'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import { RotasBackgroundServices } from 'src/rotas'
import { Api } from 'src/servicos'

import { BadgeEstado, MensagemErro } from './componentes'
import {
  Card,
  CardNumero,
  CardPonto,
  CardRotulo,
  Cards,
  colunaDuracaoStyle,
  colunaNumericaStyle,
  Dados,
  IconeAjuda,
  LinkCelula,
  NomeCelula,
  NomeComAjuda,
  Select,
  SubCelula,
  Toolbar,
  ToolbarAcoes,
  ToolbarTitulo
} from './styles'
import { formatarDuracao, numeroOuTraco } from './utils'

type Periodo = '1' | '7'

const PERIODO_DIAS: Record<Periodo, number> = { '1': 1, '7': 7 }

/** Cards de topo: contagem por categoria de estado. */
const CARDS = [
  {
    titulo: 'Falhas',
    cor: Cores.PERIGO_ERRO,
    estados: [ServiceHealthState.Failed]
  },
  {
    titulo: 'Travados',
    cor: Cores.CINZA_2,
    estados: [ServiceHealthState.Stuck, ServiceHealthState.Interrupted]
  },
  {
    titulo: 'Suspeitos',
    cor: Cores.ATENCAO,
    estados: [
      ServiceHealthState.Suspect,
      ServiceHealthState.CompletedWithErrors
    ]
  },
  {
    titulo: 'OK',
    cor: Cores.SUCESSO_CLARO,
    estados: [ServiceHealthState.Ok, ServiceHealthState.OkNoWork]
  }
]

/**
 * Prioridade de ordenação na tabela (menor = mais ao topo):
 * Running → Travado → Falha → Com erros → Suspeito → Interrompida →
 * OK → OK (sem trabalho) → Sem execução.
 */
const PRIORIDADE_POR_ESTADO: Record<ServiceHealthState, number> = {
  [ServiceHealthState.Running]: 0,
  [ServiceHealthState.Stuck]: 1,
  [ServiceHealthState.Failed]: 2,
  [ServiceHealthState.CompletedWithErrors]: 3,
  [ServiceHealthState.Suspect]: 4,
  [ServiceHealthState.Interrupted]: 5,
  [ServiceHealthState.Ok]: 6,
  [ServiceHealthState.OkNoWork]: 7,
  [ServiceHealthState.NotRun]: 8
}

export const PaginaResumoBackgroundServices: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const primeiraRender = useRef(true)
  const history = useHistory()
  const [periodo, definirPeriodo] = useState<Periodo>('1')
  const [resumo, definirResumo] = useState<ServiceSummaryResponse[]>([])

  // A Tabela já carrega no mount; só recarregamos quando o período mudar.
  useEffect(() => {
    if (primeiraRender.current) {
      primeiraRender.current = false
      return
    }
    tabelaRef.current?.CarregarDados()
  }, [periodo])

  const obterDados = async (): Promise<TabelaResposta | null> => {
    const { startDate, endDate } = Api.calcularPeriodoUtc(PERIODO_DIAS[periodo])
    const dados = await Api.ObterResumoServicos(startDate, endDate)

    if (!dados) return null

    definirResumo(dados)

    // Reordena por severidade/estado (ver PRIORIDADE_POR_ESTADO). Ordenação estável
    // preserva a ordem do backend (por última execução) dentro de cada grupo.
    const dadosOrdenados = [...dados].sort(
      (a, b) => PRIORIDADE_POR_ESTADO[a.state] - PRIORIDADE_POR_ESTADO[b.state]
    )

    return { Dados: dadosOrdenados } as TabelaResposta
  }

  const contagens = useMemo(() => {
    return CARDS.map(card => ({
      ...card,
      total: resumo.filter(s => card.estados.includes(s.state)).length
    }))
  }, [resumo])

  const abrirServico = (servico: ServiceSummaryResponse) => {
    if (servico.state === ServiceHealthState.NotRun) return
    history.push(
      `${RotasBackgroundServices.Execucoes}/${encodeURIComponent(
        servico.serviceName
      )}`
    )
  }

  const renderDicaServico = (linha: ServiceSummaryResponse) => {
    if (linha.errorMessage) {
      return <MensagemErro mensagem={linha.errorMessage} tipo="erro" />
    }
    if (linha.state === ServiceHealthState.Suspect) {
      return (
        <SubCelula tipo="alerta">
          └ encontrou {linha.totalFound}, processou {linha.totalProcessed} —
          investigar
        </SubCelula>
      )
    }
    if (linha.state === ServiceHealthState.OkNoWork) {
      return (
        <SubCelula>
          └ encontrou {linha.totalFound} · processou {linha.totalProcessed}
        </SubCelula>
      )
    }
    return null
  }

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Background Services"
          atalhos={[{ texto: 'Sistema' }, { texto: 'Background Services' }]}
        />
      </Cabecalho>

      <Cards>
        {contagens.map(card => (
          <Card key={card.titulo} corTopo={card.cor}>
            <CardNumero cor={card.cor}>{card.total}</CardNumero>
            <CardRotulo>
              <CardPonto cor={card.cor} />
              {card.titulo}
            </CardRotulo>
          </Card>
        ))}
      </Cards>

      <Toolbar>
        <ToolbarTitulo>Serviços — visão geral</ToolbarTitulo>
        <ToolbarAcoes>
          <Select
            value={periodo}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              definirPeriodo(e.target.value as Periodo)
            }
          >
            <option value="1">Hoje</option>
            <option value="7">Últimos 7 dias</option>
          </Select>
          <Botao
            type="button"
            texto="Atualizar"
            tema="Link"
            onClick={() => tabelaRef.current?.CarregarDados()}
          />
        </ToolbarAcoes>
      </Toolbar>

      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'displayName',
              RenderizarCabecalho: () => (
                <div style={{ width: 280 }}>Serviço</div>
              ),
              Renderizar: (linha: TabelaDados) => {
                const servico = linha as ServiceSummaryResponse
                const tooltipId = `desc-${servico.serviceName}`
                const clicavel = servico.state !== ServiceHealthState.NotRun
                return (
                  <div>
                    <NomeComAjuda>
                      {clicavel ? (
                        <LinkCelula onClick={() => abrirServico(servico)}>
                          <NomeCelula as="span">
                            {servico.displayName}
                          </NomeCelula>
                        </LinkCelula>
                      ) : (
                        <NomeCelula as="span">{servico.displayName}</NomeCelula>
                      )}
                      {servico.description && (
                        <IconeAjuda
                          data-tip={servico.description}
                          data-for={tooltipId}
                        >
                          ?
                        </IconeAjuda>
                      )}
                    </NomeComAjuda>
                    {renderDicaServico(servico)}
                    <Tooltip
                      id={tooltipId}
                      textColor={Cores.PRETO}
                      backgroundColor={Cores.CINZA_1_CLARO}
                      place="right"
                    />
                  </div>
                )
              }
            },
            {
              Chave: 'lastExecutionAt',
              RenderizarCabecalho: () => (
                <div style={{ width: 120 }}>Última exec.</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {linha.lastExecutionAt
                    ? formatadores.dataHoraLocaleString(linha.lastExecutionAt)
                    : '—'}
                </span>
              )
            },
            {
              Chave: 'state',
              RenderizarCabecalho: () => (
                <div style={{ width: 160 }}>Estado</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <BadgeEstado
                  tema={
                    SERVICE_HEALTH_STATE_TEMA[
                      (linha as ServiceSummaryResponse).state
                    ]
                  }
                />
              )
            },
            {
              Chave: 'totalFound',
              Alinhamento: 'center',
              StyleColuna: colunaNumericaStyle,
              RenderizarCabecalho: () => <div>Encontr.</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalFound)}</>
              )
            },
            {
              Chave: 'totalProcessed',
              Alinhamento: 'center',
              StyleColuna: colunaNumericaStyle,
              RenderizarCabecalho: () => <div>Proc.</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{numeroOuTraco(linha.totalProcessed)}</>
              )
            },
            {
              Chave: 'durationMs',
              Alinhamento: 'center',
              StyleColuna: colunaDuracaoStyle,
              RenderizarCabecalho: () => <div>Duração</div>,
              Renderizar: (linha: TabelaDados) => (
                <>{formatarDuracao(linha.durationMs)}</>
              )
            }
          ]}
          ObterDados={obterDados}
          TextoSemResultados="Nenhum serviço no período."
        />
      </Dados>
    </>
  )
}

export default PaginaResumoBackgroundServices

export { PaginaExecucoesBackgroundServices } from './execucoes'
export { PaginaItensBackgroundServices } from './itens'
